import type { ComponentType } from 'react'
import type { Chart, Coordinate, Indicator, Point } from 'klinecharts'
import type { IdeaRole } from './tradeIdeaVocab'

/** Icon component contract shared by lucide-react and our hand-drawn icons. */
export type ToolIcon = ComponentType<{
  size?: number | string
  strokeWidth?: number | string
  className?: string
}>

/**
 * A drawing-rail tool. Exactly one behavior discriminator is set:
 * - `overlay`: start a klinecharts draw with that overlay name (null = cursor)
 * - `idea`: start a trade idea in that direction (entry → stop → take profit)
 * - `panel`: toggle a docked panel rather than arm anything
 * - `note`: place an HTML note (text / callout) on the next chart click
 */
export interface ToolDef {
  id: string
  label: string
  Icon: ToolIcon
  overlay?: string | null
  /** Starts a LONG/SHORT trade idea (entry → stop loss → take profit). */
  idea?: 'buy' | 'sell'
  /** Toggles a docked panel instead of arming a tool. */
  panel?: 'tradeIdeas'
  note?: 'text' | 'callout'
  /** Tool waits for a plain chart click instead of an engine draw. */
  click?: boolean
  /** Render a separator above this tool in the rail. */
  sep?: boolean
  rotate?: boolean
  /** Render this short text label instead of the icon (e.g. "BUY"/"SELL"). */
  text?: string
  /** Extra color class applied to the label/icon (e.g. "text-success"). */
  iconClass?: string
}

export interface IndicatorOption {
  key: string
  /** klinecharts indicator id (the catalog/base name). */
  name: string
  label: string
  pane: 'main' | 'sub'
  /** Special entry handled by the workbench volume overlay, not a normal instance. */
  volume?: boolean
}

export interface IndicatorSettings {
  params: number[]
  colors: string[]
  thickness: number
  /** Legend value / price-scale tag color; defaults to the line color. */
  textColor?: string
  visible: boolean
  /** "Labels on price scale" */
  lastValueMark: boolean
  /** "Values in status line" */
  valuesInLegend: boolean
}

export interface IndicatorInstance {
  id: string
  /** Catalog/base name (display, persistence, param labels, AI context). */
  name: string
  /** Engine registry name: the base for the first instance, a registered
   * clone ("MA#2", …) for duplicates — every chart.* call uses this. */
  engineName: string
  paneId: string
  /** Companion overlays (e.g. RSI 70/50/30 reference bands). */
  bandIds: string[]
  settings: IndicatorSettings
}

export interface VolumeState {
  present: boolean
  visible: boolean
  /** null = theme default (volColors). */
  up: string | null
  down: string | null
}

export interface FibLevel {
  value: number
  color: string
  enabled: boolean
}

/** User overrides for the chart appearance; every key falls back to the theme default. */
export interface ChartSettings {
  background?: string
  gridVert?: string
  gridHoriz?: string
  crosshair?: string
  text?: string
  textSize?: number
  scale?: string
  candleUp?: string
  candleDown?: string
  candleUpBorder?: string
  candleDownBorder?: string
  candleUpWick?: string
  candleDownWick?: string
  bodyOn?: boolean
  bordersOn?: boolean
  wickOn?: boolean
}

export type ChartTypeId = 'candle' | 'bar' | 'line' | 'heikin'

/** Data-space anchor for HTML notes (timestamp is the stable key across pagination). */
export interface NotePoint {
  timestamp: number
  dataIndex?: number
  value: number
}

export interface WorkbenchNote {
  id: string
  kind: 'text' | 'callout'
  /** Bubble / text anchor. */
  pos: NotePoint
  /** Callout only: the candle-pinned tail point. */
  tail?: NotePoint
  text: string
  size: number
  bold: boolean
  /** Text color; '' = default. */
  color: string
  /** Background; '' = transparent (text) / callout default. */
  bg: string
}

/** Selected drawing snapshot handed to the floating toolbar (built in onSelected). */
export interface SelectedDrawing {
  id: string
  /** Stable render key (survives extend type-swaps). */
  key: string
  name: string
  extend?: string
  levels?: FibLevel[]
  labelMode?: 'values' | 'percent'
  fillColor?: string
  x: number
  y: number
  paneW: number
  /**
   * The sentence already written about this drawing, snapshotted as it was
   * selected. Read once, to seed the comment box — the box owns the text after
   * that, and writes it back through `saveOverlayNote`.
   *
   * Carried on the selection rather than looked up while rendering: the
   * overlays live in the chart engine, not in React state, so reading one
   * during render is a ref access that React cannot know to re-run.
   */
  note?: string
  /**
   * This selection is a drawing the trader has just FINISHED drawing, rather
   * than one they clicked. Only that one may take the keyboard — see the
   * comment box's `autoFocus`.
   */
  fresh?: boolean
}

export interface ComposerState {
  /** Anchor pixel, relative to the chart wrapper. */
  relX: number
  relY: number
  price: string
  timeLabel: string
  prec: number
  /** Overlay id whose extendData.note this composer edits. */
  editId: string | null
  initialValue?: string
  defaultLeg?: string
}

export interface HoverNote {
  id: string
  x: number
  y: number
  /** LEGACY marker leg kind, resolved through legTypes. Never set for an idea. */
  leg?: string
  label?: string
  note: string
  price: string
  /**
   * Set when the hovered overlay is a trade-idea level. Its presence picks the
   * card: trade ideas and the legacy buy/sell tool render different ones, from
   * different vocabularies, on purpose.
   */
  idea?: { role: IdeaRole; dir: 'buy' | 'sell' }
}

export interface MenuItem {
  label?: string
  icon?: ToolIcon
  onClick?: () => void
  danger?: boolean
  disabled?: boolean
  shortcut?: string
  separator?: boolean
  submenu?: Array<{ label: string; onClick: () => void; glyph?: string; tone?: string }>
}

export interface MenuSpec {
  x: number
  y: number
  items: MenuItem[]
}

/** Marker/annotation payload stored on leg-marker overlays. */
export interface LegExtendData {
  note: string
  leg: string
  label: string
  color: string
  precision: number
}

// ---------------------------------------------------------------------------
// klinecharts return-type narrowing helpers (its API returns loose unions)
// ---------------------------------------------------------------------------

/** Narrow createOverlay's `string | string[] | null` to a single id. */
export function asOverlayId(res: ReturnType<Chart['createOverlay']>): string | null {
  if (Array.isArray(res)) return res[0] ?? null
  return res ?? null
}

/** Narrow convertToPixel's single-or-array return to one complete coordinate. */
export function asPixel(
  res: Partial<Coordinate> | Array<Partial<Coordinate>>,
): { x: number; y: number } | null {
  const c = Array.isArray(res) ? res[0] : res
  if (!c || typeof c.x !== 'number' || typeof c.y !== 'number') return null
  return { x: c.x, y: c.y }
}

/** Narrow convertFromPixel's single-or-array return to one point. */
export function asPoint(
  res: Partial<Point> | Array<Partial<Point>>,
): Partial<Point> | null {
  const p = Array.isArray(res) ? res[0] : res
  return p ?? null
}

/** getIndicatorByPaneId with both args returns a single indicator; narrow it. */
export function getIndicator(chart: Chart, paneId: string, name: string): Indicator | null {
  return (chart.getIndicatorByPaneId(paneId, name) as Indicator | null) ?? null
}

// DomPosition is a const enum in klinecharts' d.ts, which can't be imported as a
// value under isolatedModules — cast the literal once here instead.
type DomPositionArg = Parameters<Chart['getDom']>[1]
const DOM_MAIN = 'main' as DomPositionArg

/** The pane's plot-area element (excludes the price axis). */
export function paneDom(chart: Chart, paneId: string): HTMLElement | null {
  return chart.getDom(paneId, DOM_MAIN)
}

/** The pane's plot-area bounding box. */
export function paneSize(chart: Chart, paneId: string) {
  return chart.getSize(paneId, DOM_MAIN)
}
